import {useRouter, useStore, humanize} from "@/plugins/obscore"
import {computed} from "vue"
import {storeToRefs} from "pinia";

// import {getFormFields} from "@/composables/CRUD/datagrid";

export default class AbstractAdmin {
    constructor(code, params) {
        this.code = code
        for (let key in params) {
            this[key] = params[key]
        }
        this.title = params.title ? params.title : humanize(code)

        this._layouts = {}

        this._routes = {}
        this.loadRoutes()

        this.store = useStore(this.model)
        this.subject = storeToRefs(this.store).subject
        this.datagrid = {
            results: computed({
                get: () => {
                    return this.store.results
                }
            }), //query results' ordered/grouped/filtered based on user choices
            fields: Object.values(this.store.fields), //list of all fields sorted by 1) grouped, 2) selected, 3) filtered, 4) none of the previous
            filters: [], //list of acutal filters; those applied to the results set
            groups: [], //list of actual groupings; those applied to the results set
            selected: [], //list of selected fields
            selectedDefault: [], //list of fields selected by default
            sorting: null,
            isLoading: computed({
                get: () => {
                    return this.store.loading
                }
            }),
            isLoaded: computed({
                get: () => {
                    return !this.store.loaded
                }
            }),
            refresh: {
                isRefreshing: false,
                lastRefresh: computed({
                    get: () => {
                        return this.store.loadedTS
                    }
                }),
                nextRefresh: computed({
                    get: () => {
                        return this.store.expireTS
                    }
                }),
                refreshAfter: computed({
                    get: () => {
                        return this.store.expireAfter
                    }
                })
            },
            selectedResults: [],
            pagination: {
                totalRecords: this.store.totalRecords,
                perPage: this.store.perPage,
                currentPage: this.store.currentPage,
                perPageOptions: [16, 32, 64],
            },
            settings: {
                groups: [], //list of groupings; those applied AND not yet applied to the results set
                filters: [], //list of filters; those applied AND not yet applied to the results set
                isDirty: false,
                show: false,
                overlay: false
            }
        }
        this.resetDatagridSettings()
    }

    getDatagrid() {
        return this.datagrid
    }

    applyDatagridSettings() {
        this.datagrid.selected = []
        this.datagrid.groups = []
        this.datagrid.settings.groups = []
        this.datagrid.filters = []
        this.datagrid.settings.filters = []
        for (let fieldName in this.store.fields) {
            const field = this.store.fields[fieldName]
            field.selected = field.shouldSelect
            if (field.selected) {
                this.datagrid.selected.push(field)
            }
            field.grouped = field.shouldGroup
            if (field.grouped) {
                this.datagrid.settings.groups.push(field)
                this.datagrid.groups.push(field)
            }
            field.filtered = field.shouldFilter
            if (field.filtered) {
                this.datagrid.settings.filters.push(field)
                this.datagrid.filters.push(field)
            }
        }
        this.sortFields()
        this.updateIsDirty()
    }

    resetDatagridSettings() {
        this.datagrid.selected = []
        this.datagrid.groups = []
        this.datagrid.settings.groups = []
        this.datagrid.filters = []
        this.datagrid.settings.filters = []
        for (let fieldName in this.store.fields) {
            const field = this.store.fields[fieldName]
            field.shouldSelect = field.selected
            if (field.selected) {
                this.datagrid.selected.push(field)
                this.datagrid.selectedDefault.push(fieldName)
            }
            field.shouldGroup = field.grouped
            if (field.grouped) {
                this.datagrid.settings.groups.push(field)
                this.datagrid.groups.push(field)
            }
            field.shouldFilter = field.filtered
            if (field.filtered) {
                this.datagrid.settings.filters.push(field)
                this.datagrid.filters.push(field)
            }
        }
        this.sortFields()
        this.updateIsDirty()
    }

    configure(route) {
        if (route.params.objectId) {
            return this.store.loadObject(route.params.objectId)
        }
        return this.store.getNewInstance()
    }

    refreshResults() {
        this.datagrid.refresh.isRefreshing = true;
        this.store.fetchResults().then(() => {
            this.datagrid.refresh.isRefreshing = false;
        })
    }

    saveSubject(redirection) {
        let object = {}
        for (var key in this.formFields) {
            object[key] = this.subject[key]
        }
        if (useRouter().currentRoute.name === this.getRoute('edit').name) {
            object.id = this.subject.id
            this.store.updateObject(object).then(() => this.handleRedirection(redirection))
        } else {
            this.store.createObject(object).then(() => this.handleRedirection(redirection))
        }
    }
    handleRedirection(redirection) {
        const routes = {0: 'edit',1:'list',2:'create'}
        let route = routes[redirection]
        let params = null
        if(redirection === 0){
            params = {"objectId":this.subject.id}
        }
        route = this.getRoute(route,params)
        route.force = true
        useRouter().push(route)
    }
    getFields() {
        return this.datagrid.fields
    }

    getFormFields() {
        for (var key in this.formFields) {
            if (!this.formFields[key] || !this.formFields[key].configured) {
                if (!this.formFields[key]) {
                    let field = this.datagrid.fields.find(x => x.name === key)
                    let component;
                    let options = {}
                    switch (field.type) {
                        case "string":
                            component = "VTextField"
                            break;
                        case "json":
                            component = "VTextArea"
                            break;
                        case "image":
                            component = "VFileInput"
                            options = {
                                accept: "image/*"
                            }
                            break;
                        case "file":
                            component = "VFileInput"
                            break;
                        case "array":
                            if (field.class === 'file') {
                                component = "VFileInput"
                                options = {
                                    multiple: true,
                                    chips: true,
                                }
                            } else {
                                // if (field.class === "string") {
                                component = "VCombobox"
                                options = {
                                    multiple: true,
                                    chips: true,
                                    items: []
                                }
                            }
                            break;
                        default:
                            component = "VTextField"
                    }
                    this.formFields[key] = {
                        "configured": true,
                        "label": field.label,
                        "component": component,
                        "options": options
                    }
                } else {
                    if (!this.formFields[key].label) {
                        let field = this.datagrid.fields.find(x => x.name === key)
                        this.formFields[key].label = field.label
                    }
                    this.formFields[key].configured = true
                }
            }
        }
        return this.formFields;
    }

    sortFields() {
        this.datagrid.fields = this.datagrid.fields.sort((f1, f2) => {
            let f1Score = 0, f2Score = 0

            if (f1.shouldGroup) f1Score -= 6
            else if (f1.grouped) f1Score -= 5
            else if (f1.shouldSelect) f1Score -= 4
            else if (f1.selected) f1Score -= 3
            else if (f1.shouldFilter) f1Score -= 2
            else if (f1.filtered) f1Score -= 1

            if (f2.shouldGroup) f2Score += 6
            else if (f2.grouped) f2Score += 5
            else if (f2.shouldSelect) f2Score += 4
            else if (f2.selected) f2Score += 3
            else if (f2.shouldFilter) f2Score += 2
            else if (f2.filtered) f2Score += 1

            return f1Score + f2Score
        })
    }

    getSelected() {
        return this.datagrid.selected
    }

    addSelected(field) {
        field.shouldSelect = true
        this.datagrid.selected.push(field)
        this.sortFields()
        return this
    }

    removeSelected(field) {
        field.shouldSelect = false
        this.datagrid.selected.popItem(field)
        this.sortFields()
        return this
    }

    clearSelected() {
        for (let fieldIndex in this.datagrid.selected) {
            this.datagrid.selected[fieldIndex].shouldSelect = false
        }
        this.datagrid.selected = []
        for (let fieldNameIndex in this.datagrid.selectedDefault) {
            this.datagrid.selected.push(this.store.fields[this.datagrid.selectedDefault[fieldNameIndex]])
            this.store.fields[this.datagrid.selectedDefault[fieldNameIndex]].shouldSelect = true
        }
        this.sortFields()
        return this
    }

    getFilters() {
        return this.datagrid.settings.filters
    }

    addFilter(field, value) {
        field.shouldFilter = true
        if (value) field.filter.value = value
        this.datagrid.settings.filters.push(field)
        this.updateIsDirty()
        this.sortFields()
        return this
    }

    applyFilter(field, value) {
        if (!field.filtered) {
            this.datagrid.filters.push(field)
        }
        if (!field.shouldFilter) {
            this.datagrid.settings.filters.push(field)
        }
        field.filtered = true
        field.shouldFilter = true
        field.filter.value = value
        this.sortFields()
    }

    removeFilter(field) {
        field.shouldFilter = false
        this.datagrid.settings.filters.popItem(field)
        this.updateIsDirty()
        return this
    }

    clearFilters() {
        for (let fieldIndex in this.datagrid.settings.filters) {
            this.datagrid.settings.filters[fieldIndex].shouldFilter = false
        }
        this.datagrid.settings.filters = []
        this.updateIsDirty()
        return this
    }

    getGroups() {
        return this.datagrid.settings.groups
    }

    addGroup(field) {
        field.shouldGroup = true
        this.datagrid.settings.groups.push(field)
        this.updateIsDirty()
        this.sortFields()
        return this
    }

    applyGroup(field) {
        if (!field.grouped) {
            this.datagrid.groups.push(field)
        }
        if (!field.shouldGroup) {
            this.datagrid.settings.groups.push(field)
        }
        field.grouped = true
        field.shouldGroup = true
        this.sortFields()
    }

    removeGroup(field) {
        field.shouldGroup = false
        this.datagrid.settings.groups.popItem(field)
        this.updateIsDirty()
        return this
    }

    clearGroups() {
        for (let fieldIndex in this.datagrid.settings.groups) {
            this.datagrid.settings.groups[fieldIndex].shouldGroup = false
        }
        this.datagrid.settings.groups = []
        this.updateIsDirty()
        return this
    }

    toggleSorting(field) {
        if (this.datagrid.sorting && this.datagrid.sorting.name != field.name) this.datagrid.sorting.sorting = null
        this.datagrid.sorting = field
        field.sorting = field.sorting == "DESC" ? "ASC" : "DESC"
    }

    getLastPage(totalRecords, perPage) {
        return Math.ceil(totalRecords / perPage);
    }

    addLayout(name, component) {
        this._layouts[name] = component
    }

    getLayout(name) {
        if (!this._layouts[name])
            this._layouts[name] = import(`@/components/${this.layouts[name]}`)


        return this._layouts[name]
    }

    loadRoutes() {
        for (let routeName in this.routes) {
            this.getRoute(routeName)
        }
    }

    createRoute(name, definition) {
        let path = `/${this.code.replaceAll(".", "/")}/${definition.path}`
        this._routes[name] = this.getRouteName(name)
        useRouter().addRoute({
            name: this._routes[name],
            path: path,
            component: () => this.getLayout(name),
            meta: {admin: this.code},
        })
    }

    getRoute(name, params) {
        if (!this._routes[name])
            this.createRoute(name, this.routes[name])
        return {
            name: this._routes[name],
            params: params
        }
    }

    getRouteName(name) {
        return `${this.code}.${name}`
    }

    getRoutes() {
        return this._routes
    }

    updateIsDirty() {
        this.datagrid.settings.isDirty = (JSON.stringify(this.datagrid.settings.filters) != JSON.stringify(this.datagrid.filters)) || (JSON.stringify(this.datagrid.settings.groups) != JSON.stringify(this.datagrid.groups))
    }

    // TODO : Add configureRoutes function for custom routes
}